// Recovery holds the origin-wide recovery lease while it waits, so its waits
// are bounded; capture's are the consult's own and stay open-ended.
export const RECOVERY_CONNECTIVITY_WAIT_MS = 30_000;
// Matches the v3 signed-form timeout.
export const RECOVERY_CHUNK_UPLOAD_TIMEOUT_MS = 60_000;
// A backup is the whole consult in one body.
export const RECOVERY_BACKUP_UPLOAD_TIMEOUT_MS = 10 * 60_000;

/** Connectivity did not return inside the bound; nothing was sent. */
export class RecoveryDeferredError extends Error {
  constructor() {
    super('recovery deferred: still offline');
    this.name = 'RecoveryDeferredError';
  }
}

/** The request ran out of time in flight; delivery is unknown. */
export class RecoveryUploadTimeoutError extends Error {
  constructor() {
    super('recovery upload timed out');
    this.name = 'RecoveryUploadTimeoutError';
  }
}

export const isRecoveryDeferredError = (err: unknown): boolean =>
  err instanceof Error && err.name === 'RecoveryDeferredError';

export const isRecoveryUploadTimeoutError = (err: unknown): boolean =>
  err instanceof Error && err.name === 'RecoveryUploadTimeoutError';

type BoundedWork<T> = (signal?: AbortSignal) => Promise<T>;

/**
 * Runs `work` under `parent`, plus a timeout when `ms` is given. Once the bound
 * fires, the rejection is replaced by `reason()`: an aborted request would
 * otherwise report a cancellation.
 */
const runBounded = <T>(
  work: BoundedWork<T>,
  parent: AbortSignal | undefined,
  ms: number | undefined,
  reason: () => Error
): Promise<T> =>
  ms === undefined ? work(parent) : runWithBound(work, parent, ms, reason);

const runWithBound = async <T>(
  work: BoundedWork<T>,
  parent: AbortSignal | undefined,
  ms: number,
  reason: () => Error
): Promise<T> => {
  const controller = new AbortController();
  const bound: { error: Error | null } = { error: null };
  const onParentAbort = () => controller.abort(parent?.reason);
  if (parent?.aborted) controller.abort(parent.reason);
  else parent?.addEventListener('abort', onParentAbort, { once: true });
  const timer = setTimeout(() => {
    bound.error = reason();
    controller.abort(bound.error);
  }, ms);
  try {
    return await work(controller.signal);
  } catch (err) {
    throw bound.error ?? err;
  } finally {
    clearTimeout(timer);
    parent?.removeEventListener('abort', onParentAbort);
  }
};

export const waitForConnectivityBounded = (
  wait: BoundedWork<void>,
  parent: AbortSignal | undefined,
  ms: number | undefined
): Promise<void> => runBounded(wait, parent, ms, () => new RecoveryDeferredError());

export const uploadBounded = <T>(
  upload: BoundedWork<T>,
  parent: AbortSignal | undefined,
  ms: number | undefined
): Promise<T> => runBounded(upload, parent, ms, () => new RecoveryUploadTimeoutError());
